// Songs: the paged, filterable track list (GET /api/track?page= in IDMode).
// Every filter parameter the server understands is exposed through the
// shared FilterBar; state lives in the URL query string.
import { useContext, useEffect, useMemo, useState } from 'react';
import { LibraryHeader } from '../components/PageComponents/header/LibraryHeader';
import { fetchPaged } from '../components/PageComponents/pagination/fetchPaged';
import { usePagedUrl, totalPageCount } from '../components/PageComponents/pagination/usePagedUrl';
import { Pagination } from '../components/PageComponents/pagination/Pagination';
import FilterBar, { readFilters, type FilterSpec, type FilterState } from '../components/PageComponents/filters/FilterBar';
import TrackList from '../components/PageComponents/trackList/TrackList';
import { Track } from '../components/PageComponents/trackList/track';
import { API_BASE } from '../config/api';
import { PlayerContext } from '../context/PlayerContext';
import '../components/PageComponents/cards/Cards.css';

const SONG_FILTER_SPEC: FilterSpec = {
    defaultSort: 'id',
    defaultOrder: 'asc',
    sorts: [
        { value: 'id', label: 'Default' },
        { value: 'title', label: 'Title' },
        { value: 'track_number', label: 'Track number' },
        { value: 'duration', label: 'Duration' },
        { value: 'album', label: 'Album' },
        { value: 'recent', label: 'Recently added' },
    ],
    selects: [
        {
            name: 'favourite',
            label: 'All tracks',
            value: '',
            options: [
                { value: 'true', label: 'Favourites only' },
                { value: 'false', label: 'Not favourited' },
            ],
        },
        {
            name: 'genre',
            label: 'All genres',
            value: '',
            options: [], // populated from /api/genre below
        },
    ],
};

export default function Songs(): React.JSX.Element {
    const { refreshTracks } = useContext(PlayerContext);
    const [songs, setSongs] = useState<Track[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState<number | null>(null);
    const [storedPerPage, setStoredPerPage] = useState<number | undefined>(undefined);
    const { page, perPage, setPage, setPerPage } = usePagedUrl({ storedPerPage });

    const [genreOptions, setGenreOptions] = useState<{ value: string; label: string }[]>([]);
    const [filterState, setFilterState] = useState<FilterState>(() => readFilters(SONG_FILTER_SPEC));
    const filterParams = useMemo(
        () => ({
            q: filterState.q,
            sort: filterState.sort,
            order: filterState.order,
            favourite: filterState.extra.favourite ?? '',
            genre: filterState.extra.genre ?? '',
        }),
        [filterState],
    );

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/genre`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((rows) => {
                if (cancelled || !Array.isArray(rows)) return;
                setGenreOptions(rows.map((g: any) => ({ value: String(g.name ?? g), label: String(g.name ?? g) })));
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/settings/ui`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : {}))
            .then((prefs: Record<string, string>) => {
                if (cancelled) return;
                const stored = Number.parseInt(prefs['ui.pagination.per_page'] ?? '', 10);
                if (!Number.isNaN(stored) && stored >= 1) setStoredPerPage(stored);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        refreshTracks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const envelope = await fetchPaged<any>('/api/track', page, perPage, filterParams);
            if (cancelled) return;
            setSongs(envelope.items.map((t: any) => Track.fromJson(t)));
            setTotal(envelope.total);
            const maxPage = totalPageCount(envelope.total, envelope.per_page);
            if (envelope.page > maxPage) {
                setPage(maxPage);
                return;
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, filterParams]);

    const totalPages = totalPageCount(total ?? 0, perPage);
    const spec = useMemo(
        () => ({
            ...SONG_FILTER_SPEC,
            selects: SONG_FILTER_SPEC.selects.map((s) => (s.name === 'genre' ? { ...s, options: genreOptions } : s)),
        }),
        [genreOptions],
    );

    return (
        <>
            <div className="pageHeader">
                <LibraryHeader eyebrow="Your library" title="Songs" />
            </div>
            <div className="pageContent">
                <FilterBar spec={spec} state={filterState} onChange={setFilterState} resultCount={total ?? undefined} />
                <TrackList items={songs} loading={loading} />
                {total !== null && totalPages > 1 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        perPage={perPage}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                    />
                )}
            </div>
        </>
    );
}